import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams } from 'react-router-dom'
import {
  api, ApiError, bridge, triggerProtocol, SERVER_BASE_URL,
  type RedirectSignInfo, type RedirectSignStatus,
} from '../lib/api'
import { SITE } from '../lib/site'

// ADR-0006 redirect-session imza sayfası — 3D-Secure'un OTP ekranının e-imza karşılığı.
//
// NOT (rota): ADR-0006 kanonik URL'i `sign.wsoft.tr/s/{id}`'dir; ancak `/s/:token`
// rotası portal alıcı-imza akışı (e-postayla gönderilen bağlantılar) tarafından zaten
// kullanılıyor. Çakışmayı önlemek için bu sayfa `/rs/:id`'ye bağlandı. Sunucunun
// ürettiği redirectUrl bu yola işaret etmeli (rapora bakınız).

const POLL_MS = 3000                  // ADR §"(Sayfa)": ~3 sn'de bir durum poll'u
const REDIRECT_DELAY_MS = 1600        // terminal durumda kısa "okuma" beklemesi
// Windows kurulum paketi — tek kaynak SITE.downloadUrl (bkz. DownloadPage).
const BRIDGE_DOWNLOAD = SITE.downloadUrl

/** Köprü, ApiError ile reddederse kullanıcıya gösterilecek kibar Türkçe mesaj. */
function bridgeErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'NO_TOKEN':
        return 'İmza aracı bulunamadı. Akıllı kartınızı/USB token’ınızı takıp tekrar deneyin.'
      case 'BUSY':
        return 'W.Sign şu anda başka bir imza işlemiyle meşgul. Lütfen birazdan tekrar deneyin.'
      case 'SESSION_NOT_SIGNABLE':
        return 'Bu imza oturumu artık imzalanabilir durumda değil. Lütfen işlemi yeniden başlatın.'
      default:
        return 'İmza tamamlanamadı. Lütfen tekrar deneyin.'
    }
  }
  return 'İmza başlatılamadı. Lütfen tekrar deneyin.'
}

function isTerminal(s: RedirectSignStatus | undefined): boolean {
  return s === 'completed' || s === 'cancelled' || s === 'expired'
}

/** "https://acme.example/yol" → "acme.example"; çözülemezse ham değeri döndürür. */
function originLabel(origin: string | null | undefined): string {
  if (!origin) return 'Bir uygulama'
  try {
    return new URL(origin).host || origin
  } catch {
    return origin
  }
}

/**
 * Yönlendirme URL'ine `session` + `status` query parametrelerini güvenle ekler.
 * Entegratör sayfası, hangi oturumun tamamlandığını bu sayede eşleştirir
 * (ör. örnek uygulama `/imza/tamam?session={id}` ile kendi kaydını bulur).
 */
function appendSessionParams(url: string, sessionId: string, status: string): string {
  try {
    const u = new URL(url)
    u.searchParams.set('session', sessionId)
    u.searchParams.set('status', status)
    return u.toString()
  } catch {
    const sep = url.includes('?') ? '&' : '?'
    return `${url}${sep}session=${encodeURIComponent(sessionId)}&status=${encodeURIComponent(status)}`
  }
}

function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, totalSeconds)
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

export default function RedirectSignPage() {
  const { id = '' } = useParams()

  const [info, setInfo]             = useState<RedirectSignInfo | null>(null)
  const [loadError, setLoadError]   = useState<'notFound' | 'network' | null>(null)
  const [busy, setBusy]             = useState(false)   // İmzala akışı sürüyor → butonu kilitle
  const [signingLocal, setSigning]  = useState(false)   // A: köprü bulundu, yerel imza sürüyor
  const [deepLinked, setDeepLinked] = useState(false)   // B: derin bağlantı fırlatıldı
  const [actionError, setActionErr] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [remaining, setRemaining]   = useState<number | null>(null)

  const status = info?.status
  const terminal = isTerminal(status)

  // ── Durum çekme ──────────────────────────────────────────────────────────────
  // `fetchInfo`'nun stabil kalması için son durumu ref'te tutuyoruz; aksi halde
  // her başarılı poll `info`'yu değiştirip poll interval'ini sıfırlardı.
  const infoRef = useRef<RedirectSignInfo | null>(null)
  useEffect(() => { infoRef.current = info }, [info])

  const fetchInfo = useCallback(async () => {
    try {
      const data = await api.redirectSignInfo(id)
      setInfo(data)
      setLoadError(null)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setLoadError('notFound')
      } else {
        // Geçici ağ hatası: elimizde durum varsa sessizce devam et, yoksa hata göster.
        setLoadError(prev => (prev === 'notFound' ? prev : (infoRef.current ? null : 'network')))
      }
    }
  }, [id])

  // İlk yükleme
  useEffect(() => { void fetchInfo() }, [fetchInfo])

  // Poll — terminal duruma veya "bulunamadı"ya kadar
  useEffect(() => {
    if (loadError === 'notFound' || terminal) return
    const iv = setInterval(() => { void fetchInfo() }, POLL_MS)
    return () => clearInterval(iv)
  }, [fetchInfo, loadError, terminal])

  // ── Kalan süre sayacı ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!info?.expiresAt) { setRemaining(null); return }
    const expiry = new Date(info.expiresAt).getTime()
    if (Number.isNaN(expiry)) { setRemaining(null); return }
    const tick = () => setRemaining(Math.max(0, Math.round((expiry - Date.now()) / 1000)))
    tick()
    const iv = setInterval(tick, 1000)
    return () => clearInterval(iv)
  }, [info?.expiresAt])

  // ── Yönlendirme (tek noktadan) ───────────────────────────────────────────────
  // Hem terminal poll durumu hem de köprünün doğrudan döndürdüğü successRedirectUrl
  // bu yardımcıyı kullanır; `redirectedRef` çift yönlendirmeyi engeller.
  const redirectedRef = useRef(false)
  const doRedirect = useCallback((base: string, status: string) => {
    if (redirectedRef.current) return
    redirectedRef.current = true
    const target = appendSessionParams(base, id, status)
    setTimeout(() => { window.location.assign(target) }, REDIRECT_DELAY_MS)
  }, [id])

  /**
   * returnMode='post' teslimi (kapalı sistemler): tarayıcı sunucunun `deliver`
   * ucuna gider; sunucu, sonucu entegratörün returnUrl'ine otomatik-POST eden bir
   * HTML form döner (3DS termUrl kalıbı). Sonuç sayfada hesaplanmaz — eylem
   * sunucudadır; biz yalnızca tarayıcıyı oraya taşırız. `redirectedRef` ile tek sefer.
   */
  const doPostDeliver = useCallback(() => {
    if (redirectedRef.current) return
    redirectedRef.current = true
    const target = `${SERVER_BASE_URL}/v1/redirect-sign/sessions/${encodeURIComponent(id)}/deliver`
    setTimeout(() => { window.location.assign(target) }, REDIRECT_DELAY_MS)
  }, [id])

  useEffect(() => {
    if (!info) return
    // completed → returnMode'a göre teslim; cancelled/expired → her zaman redirect.
    if (info.status === 'completed') {
      if (info.returnMode === 'post') {
        doPostDeliver()
      } else if (info.successRedirectUrl) {
        doRedirect(info.successRedirectUrl, info.status)
      }
      return
    }
    if (info.status === 'cancelled' || info.status === 'expired') {
      if (info.cancelRedirectUrl) doRedirect(info.cancelRedirectUrl, info.status)
    }
  }, [info, doRedirect, doPostDeliver])

  // ── Eylem: tek "İmzala" — önce A (yerel köprü), olmazsa B (derin bağlantı) ─────
  const sign = useCallback(async () => {
    if (busy) return
    setBusy(true)
    setActionErr(null)
    setDeepLinked(false)

    // A — Köprü yerelde çalışıyor mu? probe() CORS/mixed-content hatalarını yutup
    // null döndürür; null ⇒ sessizce B'ye düşeriz.
    let reachable = false
    try {
      reachable = (await bridge.probe()) !== null
    } catch {
      reachable = false
    }

    if (reachable) {
      // A — köprü kendi PIN diyaloğunu gösterecek; sayfa bu sırada poll'a devam eder.
      setSigning(true)
      try {
        const res = await bridge.redirectSign({ sessionId: id, serverBaseUrl: SERVER_BASE_URL })
        if (res.status === 'completed') {
          if (infoRef.current?.returnMode === 'post') {
            // POST teslimi: köprü yanıtındaki successRedirectUrl'e gitme; sonuç
            // sunucunun deliver ucundan entegratöre otomatik-POST edilir.
            doPostDeliver()
          } else if (res.successRedirectUrl) {
            // Sunucu GET info successRedirectUrl döndürmese bile köprü yanıtı yeterli.
            doRedirect(res.successRedirectUrl, 'completed')
          }
        }
        // Her hâlükârda kanonik durumu tazele (poll redirect'i de tetikleyebilir).
        await fetchInfo()
      } catch (err) {
        // Köprü yanıt verdi ama reddetti (NO_TOKEN/BUSY/…) → kibar mesaj, B'ye düşme.
        setActionErr(bridgeErrorMessage(err))
        setSigning(false)
      } finally {
        setBusy(false)
      }
      return
    }

    // B — köprü erişilemez: kurulu uygulamayı derin bağlantıyla uyandır.
    // Kullanıcı jesti içinde; gizli iframe "uygulama yok" hata sayfasını önler.
    triggerProtocol(`wsign://session/${id}`)
    setDeepLinked(true)
    setBusy(false)
    // Uygulama hızlıca /prepare çağırırsa durumu erken yakalamak için kısa süre sonra tazele.
    setTimeout(() => { void fetchInfo() }, 1200)
  }, [busy, id, fetchInfo, doRedirect, doPostDeliver])

  const cancel = useCallback(async () => {
    setCancelling(true)
    try {
      await api.redirectSignCancel(id)
    } catch {
      // İptal başarısız olsa bile güncel durumu çekmeyi dene.
    } finally {
      await fetchInfo()
      setCancelling(false)
    }
  }, [id, fetchInfo])

  // ── Render ───────────────────────────────────────────────────────────────────
  const title = useMemo(() => {
    if (status === 'completed') return 'İmza tamamlandı — W.Sign'
    if (status === 'cancelled') return 'İmza iptal edildi — W.Sign'
    if (status === 'expired')   return 'Oturum süresi doldu — W.Sign'
    return 'E-imza onayı — W.Sign'
  }, [status])

  return (
    <>
      <Helmet><title>{title}</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <section className="container-page py-12 md:py-16 max-w-xl">
        {loadError === 'notFound'
          ? <InvalidCard />
          : loadError === 'network' && !info
            ? <NetworkErrorCard onRetry={() => { void fetchInfo() }} />
            : !info
              ? <LoadingCard />
              : <SessionView
                  info={info}
                  remaining={remaining}
                  busy={busy}
                  signingLocal={signingLocal}
                  deepLinked={deepLinked}
                  actionError={actionError}
                  cancelling={cancelling}
                  onSign={sign}
                  onCancel={cancel}
                />}
      </section>
    </>
  )
}

// ──────────────────────────────────────────────────────────────────────────────

function SessionView({
  info, remaining, busy, signingLocal, deepLinked, actionError, cancelling, onSign, onCancel,
}: {
  info: RedirectSignInfo
  remaining: number | null
  busy: boolean
  signingLocal: boolean
  deepLinked: boolean
  actionError: string | null
  cancelling: boolean
  onSign: () => void
  onCancel: () => void
}) {
  const { status } = info
  const expired = remaining !== null && remaining <= 0

  if (status === 'completed') {
    // POST teslimi: tarayıcı sunucunun deliver ucuna gider; successRedirectUrl
    // bilinmese de teslim sunucuda gerçekleşir.
    const completedMessage =
      info.returnMode === 'post'
        ? 'İmza tamamlandı, uygulamanıza dönülüyor…'
        : info.successRedirectUrl
          ? 'Birkaç saniye içinde yönlendirileceksiniz…'
          : 'Bu sekmeyi kapatabilirsiniz; işlemi başlatan uygulama bilgilendirildi.'
    return (
      <div className="card border-emerald-200 bg-emerald-50" role="status" aria-live="polite">
        <h1 className="text-xl font-semibold text-emerald-900">İmza tamamlandı</h1>
        <p className="mt-2 text-sm text-emerald-800">
          Belgeniz başarıyla imzalandı.{' '}
          {completedMessage}
        </p>
      </div>
    )
  }

  if (status === 'cancelled') {
    return (
      <div className="card" role="status" aria-live="polite">
        <h1 className="text-xl font-semibold">İmza iptal edildi</h1>
        <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
          İmza işlemi iptal edildi.{' '}
          {info.cancelRedirectUrl
            ? 'Birkaç saniye içinde yönlendirileceksiniz…'
            : 'Bu sekmeyi kapatabilirsiniz.'}
        </p>
      </div>
    )
  }

  if (status === 'expired') {
    return (
      <div className="card border-amber-200 bg-amber-50" role="status" aria-live="polite">
        <h1 className="text-xl font-semibold text-amber-900">Oturum süresi doldu</h1>
        <p className="mt-2 text-sm text-amber-800">
          Bu imza oturumunun süresi doldu.{' '}
          {info.cancelRedirectUrl
            ? 'Birkaç saniye içinde yönlendirileceksiniz…'
            : 'Lütfen işlemi başlatan uygulamaya dönüp yeni bir imza talebi başlatın.'}
        </p>
      </div>
    )
  }

  // pending | awaitingSignature | signing — sunucu ilerlemesi VEYA yerel köprü imzası.
  const inProgress = status === 'awaitingSignature' || status === 'signing' || signingLocal

  return (
    <div className="space-y-6">
      <div className="card">
        <span className="badge badge-info">E-imza onayı</span>
        <h1 className="mt-3 text-2xl font-semibold">
          <strong>{originLabel(info.origin)}</strong> sizden e-imza istiyor
        </h1>
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex items-start justify-between gap-4">
            <dt className="text-[var(--color-fg-muted)]">Belge</dt>
            <dd className="font-medium text-right break-all">{info.documentName || 'Belge'}</dd>
          </div>
          {remaining !== null && (
            <div className="flex items-center justify-between gap-4">
              <dt className="text-[var(--color-fg-muted)]">Kalan süre</dt>
              <dd
                className={`font-mono tabular-nums ${expired ? 'text-[var(--color-err)]' : 'text-[var(--color-fg)]'}`}
                aria-label={`Kalan süre ${formatCountdown(remaining)}`}
              >
                <span aria-hidden="true">{formatCountdown(remaining)}</span>
              </dd>
            </div>
          )}
        </dl>
      </div>

      <div className="card" aria-live="polite">
        {inProgress ? (
          <div role="status">
            <h2 className="text-lg font-semibold">İmzalanıyor…</h2>
            <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
              W.Sign uygulamasında açılan pencereden sertifikanızı seçip PIN'inizi girin.
              İmzanız alınınca bu sayfa otomatik güncellenecek.
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-semibold">İmzalamak için "İmzala"ya basın</h2>
            <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
              "İmzala"ya bastığınızda W.Sign uygulaması imza penceresini açar; sertifikanızı
              seçip PIN'inizi girerek imzayı tamamlarsınız. Belgeniz tarayıcıya inmez.
            </p>
            <div className="mt-4">
              <button type="button" onClick={onSign} disabled={expired || busy} className="btn-primary">
                {busy ? 'Açılıyor…' : 'İmzala'}
              </button>
            </div>
            {deepLinked && (
              <p className="mt-3 text-sm text-[var(--color-fg-muted)]" role="status">
                W.Sign uygulaması açılıyor… Açılmazsa aşağıdan kurun.
              </p>
            )}
            {actionError && (
              <p className="mt-3 text-sm text-[var(--color-err)]" role="alert">
                {actionError}
              </p>
            )}
          </>
        )}
      </div>

      {/* Fallback — W.Sign kurulu değilse */}
      <details className="card">
        <summary className="cursor-pointer text-sm font-medium">
          W.Sign açılmadı mı?
        </summary>
        <p className="mt-3 text-sm text-[var(--color-fg-muted)]">
          İmza için bilgisayarınızda W.Sign uygulamasının kurulu olması gerekir.
          Kurulu değilse indirip kurun, ardından bu sayfaya dönüp "Tekrar dene"ye basın.
        </p>
        <p className="mt-2 text-xs text-[var(--color-fg-dim)]">
          İlk kurulumda Windows SmartScreen "Bilinmeyen yayıncı" uyarısı verebilir;
          "Daha fazla bilgi → Yine de çalıştır" ile devam edin.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <a href={BRIDGE_DOWNLOAD} className="btn-primary" target="_blank" rel="noopener">
            W.Sign'ı indir (Windows)
          </a>
          <button type="button" onClick={onSign} disabled={expired || busy} className="btn-ghost">
            Tekrar dene
          </button>
        </div>
      </details>

      <div className="flex justify-center">
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelling}
          className="text-sm text-[var(--color-fg-muted)] underline underline-offset-2 hover:text-[var(--color-fg)] disabled:opacity-60"
        >
          {cancelling ? 'İptal ediliyor…' : 'İmzadan vazgeç'}
        </button>
      </div>
    </div>
  )
}

function LoadingCard() {
  return (
    <div className="card" role="status" aria-live="polite">
      <p className="text-sm text-[var(--color-fg-muted)]">İmza oturumu yükleniyor…</p>
    </div>
  )
}

function InvalidCard() {
  return (
    <div className="card border-red-200 bg-red-50" role="alert">
      <h1 className="text-xl font-semibold text-red-800">Oturum geçersiz</h1>
      <p className="mt-2 text-sm text-red-700">
        Bu imza bağlantısı geçersiz veya süresi dolmuş. Lütfen işlemi başlatan
        uygulamaya dönüp yeni bir imza talebi başlatın.
      </p>
    </div>
  )
}

function NetworkErrorCard({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="card border-amber-200 bg-amber-50" role="alert">
      <h1 className="text-xl font-semibold text-amber-900">Bağlantı kurulamadı</h1>
      <p className="mt-2 text-sm text-amber-800">
        İmza oturumuna ulaşılamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.
      </p>
      <button type="button" onClick={onRetry} className="btn-primary mt-4">
        Tekrar dene
      </button>
    </div>
  )
}
